import type { ReactNode } from 'react'
import type { LabelProps, PieLabelRenderProps } from 'recharts'
import { Badge } from '@/components/ui/badge'
import {
  REPORT_PIE_CHART_COLORS,
  formatPieSharePercent,
} from '@/components/reports/report-chart-utils'
export { downloadExportedReport } from '@/lib/download'
import { formatCurrency, readProperty } from '@/lib/utils'

export { REPORT_PIE_CHART_COLORS, formatPieSharePercent }

export async function getExportErrorMessage(
  error: unknown,
  fallbackMessage = 'Failed to export report'
): Promise<string> {
  const responseData = readProperty(readProperty(error, 'response'), 'data')

  if (!(responseData instanceof Blob)) {
    return readResponseMessage(responseData) || fallbackMessage
  }

  try {
    const text = await responseData.text()

    if (!text) {
      return fallbackMessage
    }

    const parsed: unknown = JSON.parse(text)

    return readResponseMessage(parsed) || fallbackMessage
  } catch {
    return fallbackMessage
  }
}

// API error bodies carry `message` as a string.
function readResponseMessage(body: unknown): string | undefined {
  const message = readProperty(body, 'message')

  return typeof message === 'string' ? message : undefined
}

type PieShareLabelSource = Partial<
  Pick<PieLabelRenderProps, 'cx' | 'cy' | 'midAngle' | 'outerRadius' | 'percent'>
>

type PieShareLabelPosition = {
  x: number
  y: number
  textAnchor: 'start' | 'end'
  label: string
}

export function renderPieShareLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
}: PieLabelRenderProps) {
  const labelProps = getPieShareLabelProps({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
  })

  if (!labelProps) {
    return null
  }

  return (
    <text
      x={labelProps.x}
      y={labelProps.y}
      fill="currentColor"
      textAnchor={labelProps.textAnchor}
      dominantBaseline="central"
      className="fill-foreground text-[11px] font-medium tabular-nums"
    >
      {labelProps.label}
    </text>
  )
}

export function getPieShareLabelProps({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
}: PieShareLabelSource): PieShareLabelPosition | null {
  if (
    typeof cx !== 'number' ||
    typeof cy !== 'number' ||
    typeof midAngle !== 'number' ||
    typeof outerRadius !== 'number' ||
    typeof percent !== 'number' ||
    percent <= 0
  ) {
    return null
  }

  const label = formatPieSharePercent(percent)
  const radius = outerRadius + 20
  const rawX = cx + radius * Math.cos((-midAngle * Math.PI) / 180)
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180)
  const labelInset = label.length * 7 + 10
  const isRightSide = rawX > cx
  const x = isRightSide ? Math.min(rawX, cx * 2 - labelInset) : Math.max(rawX, labelInset)

  return {
    x,
    y,
    textAnchor: isRightSide ? 'start' : 'end',
    label,
  }
}

type ReportPieCenterLabelProps = {
  viewBox: LabelProps['viewBox']
  totalValue: number
  label: ReactNode
}

export function ReportPieCenterLabel({ viewBox, totalValue, label }: ReportPieCenterLabelProps) {
  // Only a polar (pie) view box has a center.
  if (
    !viewBox ||
    !('cx' in viewBox) ||
    typeof viewBox.cx !== 'number' ||
    typeof viewBox.cy !== 'number'
  ) {
    return null
  }

  return (
    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
      <tspan
        x={viewBox.cx}
        y={viewBox.cy}
        className="fill-foreground text-base font-semibold tabular-nums"
      >
        {formatCurrency(totalValue)}
      </tspan>
      <tspan x={viewBox.cx} y={viewBox.cy + 18} className="fill-muted-foreground text-xs">
        {label}
      </tspan>
    </text>
  )
}

export function ReportCountBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="secondary" size="sm">
      {children}
    </Badge>
  )
}
